import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "react-query";
import { OverlayView } from "@react-google-maps/api";
import { renderPolygonsOnMap, updateSelectedRoutesOnDataLayer } from "components/audience-map/utils";
import { MAP_EXTRA_STYLES } from "components/audience-map/options";
import { MAP_TYPES } from "pages/constants";
import { Button } from "components/ui/button";
import useOverlaysFetch from "Hooks/useOverlaysFetch";
import { useClientConfig } from "campaign-builder/context/ClientConfigContext";
import { MapFrame, useMapInstance } from "./map/MapFrame";
import { LoadingOverlay, ErrorOverlay, EmptyBanner } from "./map/MapStates";
import { MapToolbar } from "./map/MapToolbar";
import { MapSearch, type MapSearchSelection } from "./map/MapSearch";
import { ExpandMapModal } from "./ExpandMapModal";
import { useTargeting } from "./TargetingContext";
import { fetchD2dBoundaries, type D2dBoundary } from "./d2dBoundaries";
import { isInsideZones } from "./h2hLocations";

// Zoom the D2D map applies when a search result is picked (legacy parity).
const D2D_SEARCH_ZOOM = 15;

// Normalises a boundary's `paths` to a {lat,lng} ring for a containment test.
// Boundaries arrive as [{lat,lng}…] (per d2dBoundaries); the other shapes are
// defensive (a [[…]] wrapper, or GeoJSON [lng,lat] pairs).
const toLatLngRing = (paths: unknown): Array<{ lat: number; lng: number }> => {
  if (!Array.isArray(paths) || paths.length === 0) return [];
  const first = paths[0] as any;
  if (typeof first?.lat === "number" && typeof first?.lng === "number") {
    return paths as Array<{ lat: number; lng: number }>;
  }
  if (Array.isArray(first)) {
    if (typeof first[0] === "number") {
      return (paths as Array<[number, number]>).map(([lng, lat]) => ({ lat, lng }));
    }
    return toLatLngRing(first);
  }
  return [];
};

const D2D_MAP_OPTIONS = { styles: MAP_EXTRA_STYLES };
const FOCUS_MS = 1500;

// Draws the fetched mission-boundary polygons onto the map's data layer using
// the legacy renderer (so hover/select colours are byte-identical to the old
// D2D map) and bridges selection into TargetingContext. Lives inside MapFrame so
// it can reach the map instance + the shared pan helper.
// `active` is false for the inline layer while the Expand modal is open, so the
// shared search signal only "clicks" the polygon on the map the user is looking
// at — otherwise both instances would open a pending Add dialog and the inline
// one would linger after the modal closes.
const D2dPolygonLayer = ({ boundaries, active }: { boundaries: D2dBoundary[]; active: boolean }) => {
  const { map, panTo } = useMapInstance();
  const {
    selectedAreas,
    addArea,
    backfillAreaUnits,
    highlightArea,
    mapFocusAreaId,
    clearMapFocus,
    hoveredAreaId,
    setHoveredArea,
    mapSearchPoint,
    clearMapSearchPoint,
  } = useTargeting();
  const renderedKeyRef = useRef<string | null>(null);
  const focusTimer = useRef<ReturnType<typeof setTimeout>>();
  // The polygon clicked but not yet confirmed — drives the on-map "Add area for
  // distribution?" dialog. Mirrors the H2H pin tooltip: selection commits only
  // on Add, not on the raw map click.
  const [pendingId, setPendingId] = useState<string | null>(null);
  const pendingIdRef = useRef<string | null>(null);

  useEffect(() => () => clearTimeout(focusTimer.current), []);

  const byId = useMemo(() => new Map(boundaries.map((b) => [b.id, b])), [boundaries]);

  // Pre-redesign drafts persisted areas without missionCount, so they rehydrate
  // with no units and the cards + Summary letterbox count read 0. The boundary
  // layer is the source of per-area delivery units (T8) — once it's loaded,
  // patch the missing ones (a no-op for healthy selections).
  useEffect(() => {
    if (!selectedAreas.some((a) => typeof a.units !== "number" && byId.has(a.id))) return;
    backfillAreaUnits(new Map([...byId].map(([id, b]) => [id, b.units])));
  }, [byId, selectedAreas, backfillAreaUnits]);

  // Previews a polygon while its Add dialog is open by toggling the legacy
  // `hovered` highlight; `selected` is left to the selection sync so committed
  // state never depends on dialog state.
  const setHovered = useCallback(
    (id: string | null, hovered: boolean) => {
      if (!map || !id) return;
      map.data.forEach((f) => {
        if (String(f.getProperty("id")) === id) f.setProperty("hovered", hovered);
      });
    },
    [map]
  );

  // Clears the pending preview (no commit), restoring the polygon's resting look.
  const dismissPending = useCallback(() => {
    setHovered(pendingIdRef.current, false);
    pendingIdRef.current = null;
    setPendingId(null);
  }, [setHovered]);

  // Click handler routed through a ref so the data-layer click listener (bound
  // once at render) always sees the latest selection state instead of a stale
  // closure over selectedAreas.
  const selectRef = useRef<(args: { missionId: string; paths: unknown; selected: boolean }) => void>(() => undefined);
  selectRef.current = ({ missionId, selected }) => {
    const id = String(missionId);
    // The legacy renderer optimistically flips `selected`; we drive selection
    // ourselves, so revert it and decide based on the area's real state.
    const isCommitted = selectedAreas.some((a) => a.id === id);
    map?.data.forEach((f) => {
      if (String(f.getProperty("id")) === id) f.setProperty("selected", isCommitted);
    });

    if (isCommitted) {
      // Clicking a committed area highlights its card (two-way map↔list);
      // removal is trash-only, matching H2H.
      highlightArea(id);
      return;
    }
    // Unselected polygon → open the Add dialog; commit happens on Add. Without a
    // centroid there's nowhere to anchor the dialog, so commit immediately.
    const boundary = byId.get(id);
    if (!boundary?.center) {
      addArea({
        id,
        value: "Area",
        units: boundary?.units,
        paths: boundary?.paths,
        type: MAP_TYPES.POLYGON,
      });
      return;
    }
    if (pendingIdRef.current && pendingIdRef.current !== id) setHovered(pendingIdRef.current, false);
    setHovered(id, true);
    pendingIdRef.current = id;
    setPendingId(id);
  };
  const handleMissionSelect = useCallback(
    (args: { missionId: string; paths: unknown; selected: boolean; isVisible?: boolean }) => selectRef.current(args),
    []
  );

  const handleAddPending = useCallback(() => {
    const id = pendingIdRef.current;
    if (!id) return;
    const boundary = byId.get(id);
    setHovered(id, false);
    pendingIdRef.current = null;
    setPendingId(null);
    addArea({
      id,
      value: "Area",
      units: boundary?.units,
      paths: boundary?.paths,
      lat: boundary?.center?.lat,
      lng: boundary?.center?.lng,
      type: MAP_TYPES.POLYGON,
    });
  }, [byId, setHovered, addArea]);

  // Clicking the map background (not a polygon) dismisses the Add dialog.
  useEffect(() => {
    if (!map) return;
    const listener = map.addListener("click", () => dismissPending());
    return () => listener.remove();
  }, [map, dismissPending]);

  // Render the polygons once per boundary set. Clearing prior features +
  // listeners first keeps a city switch from stacking duplicate layers/handlers.
  useEffect(() => {
    if (!map || !boundaries.length) return;
    const key = boundaries.map((b) => b.id).join("|");
    if (renderedKeyRef.current === key) return;
    renderedKeyRef.current = key;

    map.data.forEach((f) => map.data.remove(f));
    window.google.maps.event.clearListeners(map.data, "click");
    window.google.maps.event.clearListeners(map.data, "mouseover");
    window.google.maps.event.clearListeners(map.data, "mouseout");

    const missionBoundaries = boundaries.reduce<Record<string, any>>((acc, b) => {
      acc[b.id] = { id: b.id, paths: b.paths, name: b.name, cityId: null };
      return acc;
    }, {});

    renderPolygonsOnMap({
      mapInit: map,
      activeCityMissionBoundaries: missionBoundaries,
      handleMissionSelect,
      activeSelectedCityAddresses: selectedAreas.map((a) => ({ id: a.id, isVisible: true })),
      isSubmitted: false,
      // Polygon mouseover paints it red (legacy renderer) and notifies the list so
      // the matching card outlines too (AC24). setHoveredArea is stable.
      setHoveredMission: (id: unknown) => setHoveredArea(id ? String(id) : null),
    });
    // selectedAreas intentionally omitted: the dedicated sync effect below keeps
    // the painted selection in step without forcing a full re-render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, boundaries, handleMissionSelect]);

  // Keep the painted selection in sync when areas are added/removed elsewhere
  // (View, card trash, rehydrate). Setting the feature property restyles it.
  useEffect(() => {
    if (!map || renderedKeyRef.current === null) return;
    updateSelectedRoutesOnDataLayer({ dataLayer: map.data, selectedRoutes: selectedAreas.map((a) => ({ id: a.id })) });
  }, [map, selectedAreas]);

  // Reflect the hovered area onto the polygons (AC24): hovering a list card fills
  // its polygon red. Single source of truth — the legacy mouseover sets the same
  // `hovered` flag, so map-side hover converges here too; clearing repaints all.
  useEffect(() => {
    if (!map || renderedKeyRef.current === null) return;
    map.data.forEach((f) => {
      const isHovered = hoveredAreaId !== null && String(f.getProperty("id")) === hoveredAreaId;
      if (!!f.getProperty("hovered") !== isHovered) f.setProperty("hovered", isHovered);
    });
  }, [map, hoveredAreaId]);

  // A list card asked to locate its polygon: pan to its centroid and flash it
  // (hovered = red) briefly, matching the H2H focus behaviour. The revert timer
  // lives in a ref (not the effect cleanup) because clearMapFocus re-runs this
  // effect immediately — a cleanup-owned timer would be cancelled before it
  // fires, leaving the polygon stuck red.
  useEffect(() => {
    if (!map || !mapFocusAreaId) return;
    const id = mapFocusAreaId;
    const area = selectedAreas.find((a) => a.id === id);
    clearMapFocus();
    if (!area || typeof area.lat !== "number" || typeof area.lng !== "number") return;
    panTo({ lat: area.lat, lng: area.lng });
    map.data.forEach((f) => {
      if (String(f.getProperty("id")) === id) f.setProperty("hovered", true);
    });
    clearTimeout(focusTimer.current);
    focusTimer.current = setTimeout(() => {
      map.data.forEach((f) => {
        if (String(f.getProperty("id")) === id) f.setProperty("hovered", false);
      });
    }, FOCUS_MS);
  }, [map, mapFocusAreaId, selectedAreas, panTo, clearMapFocus]);

  // The map search handed us a point: find the boundary containing it and open
  // that area's Add dialog — the same outcome as clicking the polygon (AC-S3).
  // Already-selected areas just get their card highlighted; a centre-less match
  // commits immediately (mirroring the click path); a point in no boundary is a
  // no-op (the search's own out-of-area message covers it).
  useEffect(() => {
    if (!active || !map || !mapSearchPoint || renderedKeyRef.current === null) return;
    const point = { lat: mapSearchPoint.lat, lng: mapSearchPoint.lng };
    clearMapSearchPoint();
    const g = window.google?.maps;
    if (!g) return;
    const latLng = new g.LatLng(point.lat, point.lng);
    const boundary = boundaries.find((b) => {
      const ring = toLatLngRing(b.paths);
      return ring.length >= 3 && g.geometry.poly.containsLocation(latLng, new g.Polygon({ paths: ring }));
    });
    if (!boundary) return;
    if (selectedAreas.some((a) => a.id === boundary.id)) {
      highlightArea(boundary.id);
      return;
    }
    if (!boundary.center) {
      addArea({
        id: boundary.id,
        value: "Area",
        units: boundary.units,
        paths: boundary.paths,
        type: MAP_TYPES.POLYGON,
      });
      return;
    }
    if (pendingIdRef.current && pendingIdRef.current !== boundary.id) setHovered(pendingIdRef.current, false);
    setHovered(boundary.id, true);
    pendingIdRef.current = boundary.id;
    setPendingId(boundary.id);
    panTo(boundary.center);
  }, [
    active,
    map,
    mapSearchPoint,
    boundaries,
    selectedAreas,
    clearMapSearchPoint,
    highlightArea,
    addArea,
    setHovered,
    panTo,
  ]);

  const pending = pendingId ? byId.get(pendingId) : undefined;
  if (!pending?.center) return null;

  return (
    <OverlayView
      position={pending.center}
      mapPaneName={OverlayView.FLOAT_PANE}
      getPixelPositionOffset={() => ({ x: 0, y: 0 })}
    >
      {/* Lifted above the polygon centroid (transform, not pixel-offset, so it
          doesn't jump on first paint) so it doesn't cover the area. Confirms
          before committing. stopPropagation keeps the click off the map — without
          it the mouseup falls through and can re-open the dialog. */}
      <div
        style={{ transform: "translate(-50%, calc(-100% - 12px))" }}
        className="flex w-[320px] items-center gap-5 rounded-lg border border-solid border-border bg-background p-4 shadow-[0px_10px_15px_rgba(0,0,0,0.15)]"
        data-cypress-id="targeting-area-add-dialog"
        onMouseDown={(e) => e.stopPropagation()}
        onMouseUp={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-foreground">Add area for distribution?</p>
          {pending.name && <p className="truncate text-xs text-muted-foreground">{pending.name}</p>}
        </div>
        <Button
          type="button"
          className="h-9 shrink-0 px-4"
          onClick={handleAddPending}
          data-cypress-id="targeting-area-add-confirm"
        >
          Add
        </Button>
      </div>
    </OverlayView>
  );
};

// The in-map search for D2D (AC-S1/AC-S3): selecting an address pans/zooms the
// map there so the user can pick the polygon — D2D areas are fixed mission
// boundaries, so nothing is auto-created. If the address falls outside the
// city's distribution zones a service-area message is shown. Lives inside
// MapFrame so it can drive the shared map instance.
const D2dMapSearch = () => {
  const { map, panTo } = useMapInstance();
  const { selectedCity, recommendedZones, searchMapPoint } = useTargeting();
  const { country } = useClientConfig();
  const [outOfZone, setOutOfZone] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => () => clearTimeout(timer.current), []);

  const handleSelect = useCallback(
    ({ lat, lng }: MapSearchSelection) => {
      panTo({ lat, lng });
      map?.setZoom(D2D_SEARCH_ZOOM);
      const inside = isInsideZones({ lat, lng }, recommendedZones);
      setOutOfZone(!inside);
      clearTimeout(timer.current);
      if (!inside) timer.current = setTimeout(() => setOutOfZone(false), 6000);
      // Hand the point to the polygon layer: it finds the boundary containing the
      // address and opens that area's Add dialog (the search "clicks" the area).
      searchMapPoint({ lat, lng });
    },
    [panTo, map, recommendedZones, searchMapPoint]
  );

  return (
    <div className="flex flex-col gap-2">
      <MapSearch city={selectedCity} countryCode={country?.code ?? ""} onSelect={handleSelect} />
      {outOfZone && (
        <div
          className="pointer-events-auto rounded-lg border border-solid border-[#fee685] dark:border-[#973c00] bg-[#fffbeb] dark:bg-[#461901] px-3 py-2 text-sm text-[#7b3306] dark:text-[#fef3c6] shadow-sm"
          data-cypress-id="targeting-map-out-of-zone"
        >
          This address is outside the distribution service area for this city.
        </div>
      )}
    </div>
  );
};

// D2D targeting map (F5): selectable distribution polygons. Reuses the existing
// polygon look/colours via the legacy data-layer renderer while wiring selection
// into the unified TargetingContext (Area N cards, toast + View) — no legacy
// Zustand store. The `modal` variant is the same map inside the Expand-map modal
// (search but no nested expand control — AC26).
export const D2dMap = ({ variant = "inline" }: { variant?: "inline" | "modal" } = {}) => {
  const { selectedCity, recommendedZones } = useTargeting();
  const { country } = useClientConfig();
  const countryCode = country?.code ?? "";
  const { loadData } = useOverlaysFetch({ countryCode, disabled: !countryCode });
  const isModal = variant === "modal";
  const [expandOpen, setExpandOpen] = useState(false);

  const cityId = selectedCity?.id;

  // The recommended zones clip the boundary fetch (non-US), so they're part of
  // the cache identity — keyed by their ids so the set populating mid-load drives
  // exactly one refetch, then caches. Returning to Targeting after a step switch
  // is then a cache hit: no refetch, no loading flash (PAD-31560). Boundaries are
  // deterministic per (city, zones) and unaffected by area selection, so
  // staleTime is Infinity. Trade-off by design: no success-state refresh within a
  // builder session (that would defeat the no-refetch-on-return goal); picking a
  // different city is the refresh path, and the error state still offers Retry.
  const zoneKey = useMemo(() => recommendedZones.map((z) => z.id).join("|"), [recommendedZones]);
  const {
    data: boundaries = [],
    isLoading: loading,
    isError: error,
    refetch: refetchBoundaries,
  } = useQuery({
    queryKey: ["targeting-d2d-boundaries", cityId, countryCode, zoneKey],
    queryFn: () => fetchD2dBoundaries({ city: selectedCity!, zones: recommendedZones, countryCode, loadData }),
    enabled: !!cityId && !!selectedCity && !!countryCode,
    staleTime: Infinity,
  });

  const center = useMemo(
    () => (selectedCity ? { lat: selectedCity.lat, lng: selectedCity.lng } : undefined),
    [selectedCity]
  );

  const ready = !loading && !error;
  const overlays = (
    <>
      {loading && <LoadingOverlay label="Loading distribution areas" />}
      {error && <ErrorOverlay message="Couldn't load distribution areas." onRetry={() => void refetchBoundaries()} />}
      {ready && boundaries.length === 0 && <EmptyBanner message="No distribution areas available for this city." />}
      <MapToolbar
        title={isModal ? undefined : `Distribution areas in ${selectedCity?.name ?? ""}`}
        expandable={!isModal}
        onExpand={() => setExpandOpen(true)}
      >
        <D2dMapSearch />
      </MapToolbar>
    </>
  );

  return (
    <>
      <MapFrame
        cypressId={isModal ? "targeting-map-d2d-modal" : "targeting-map-d2d"}
        center={center}
        options={D2D_MAP_OPTIONS}
        overlays={overlays}
      >
        {ready && <D2dPolygonLayer boundaries={boundaries} active={isModal || !expandOpen} />}
      </MapFrame>
      {!isModal && (
        <ExpandMapModal open={expandOpen} onClose={() => setExpandOpen(false)} cityName={selectedCity?.name}>
          <D2dMap variant="modal" />
        </ExpandMapModal>
      )}
    </>
  );
};
